import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type {
  ChatAccessMode,
  ChatAccessPolicy,
  ChatAccessRule,
  ChatAudienceTier,
  PrivateChatPricing,
} from '@fetmode/types';
import { CHAT_ACCESS_MODES, CHAT_AUDIENCE_TIERS } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

const DEFAULT_POLICY: ChatAccessPolicy = {
  dormantDays: 90,
  rules: {
    GUEST: { mode: 'DISABLED' },
    NEVER_PURCHASED: { mode: 'MESSAGE_CAP', messageCap: 3 },
    DORMANT: { mode: 'MESSAGE_CAP', messageCap: 3 },
    NO_BALANCE: { mode: 'TIP_FIRST' },
    HAS_BALANCE: { mode: 'ALLOWED' },
  },
};

const DEFAULT_PRIVATE_PRICING: PrivateChatPricing = {
  enabled: true,
  textCoins: 0,
  imageCoins: 50,
  stickerCoins: 5,
};

function tierLabel(tier: ChatAudienceTier): string {
  return t(`common.studio.live.chatTier.${tier}`);
}

function modeLabel(mode: ChatAccessMode): string {
  return t(`common.studio.live.chatMode.${mode}`);
}

function updateRule(
  policy: ChatAccessPolicy,
  tier: ChatAudienceTier,
  patch: Partial<ChatAccessRule>,
): ChatAccessPolicy {
  const current = policy.rules[tier];
  const next: ChatAccessRule = { ...current, ...patch };
  if (next.mode === 'MESSAGE_CAP' && next.messageCap === undefined) {
    next.messageCap = current.messageCap ?? 3;
  }
  if (next.mode !== 'MESSAGE_CAP') {
    delete next.messageCap;
  }
  return {
    ...policy,
    rules: {
      ...policy.rules,
      [tier]: next,
    },
  };
}

function parseCoinInput(value: string, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.min(10_000, parsed);
}

export function ModelChatSettingsCard(): ReactElement {
  const { api } = useAuth();
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [chatAccessPolicy, setChatAccessPolicy] = useState<ChatAccessPolicy>(DEFAULT_POLICY);
  const [privateChatPricing, setPrivateChatPricing] =
    useState<PrivateChatPricing>(DEFAULT_PRIVATE_PRICING);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void api
      .getModelChatConfig()
      .then((result) => {
        if (cancelled) {
          return;
        }
        setWelcomeMessage(result.config.welcomeMessage ?? '');
        setChatAccessPolicy(result.config.chatAccessPolicy);
        setPrivateChatPricing(result.config.privateChatPricing ?? DEFAULT_PRIVATE_PRICING);
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('common.studio.dashboard.chatSettingsLoadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await api.updateModelChatConfig({
        welcomeMessage: welcomeMessage.trim().length > 0 ? welcomeMessage.trim() : null,
        chatAccessPolicy,
        privateChatPricing,
      });
      setWelcomeMessage(result.config.welcomeMessage ?? '');
      setChatAccessPolicy(result.config.chatAccessPolicy);
      setPrivateChatPricing(result.config.privateChatPricing ?? DEFAULT_PRIVATE_PRICING);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.dashboard.chatSettingsSaveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1.125rem' }}>{t('common.studio.dashboard.chatSettingsTitle')}</h2>
      <p style={{ marginTop: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.dashboard.chatAccessHint')}
      </p>

      {isLoading ? (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.dashboard.loading')}</p>
      ) : (
        <>
          <label style={{ display: 'block', marginBottom: '0.75rem' }}>
            {t('common.studio.live.chatWelcomeMessage')}
            <input
              className="fm-input"
              value={welcomeMessage}
              maxLength={200}
              placeholder="Welcome {{username}}! 💕"
              onChange={(event) => {
                setWelcomeMessage(event.target.value);
              }}
            />
          </label>

          <label style={{ display: 'block', marginBottom: '1rem' }}>
            {t('common.studio.live.chatDormantDays')}
            <input
              className="fm-input"
              type="number"
              min={1}
              max={365}
              value={String(chatAccessPolicy.dormantDays)}
              onChange={(event) => {
                const parsed = Number.parseInt(event.target.value, 10);
                setChatAccessPolicy((current) => ({
                  ...current,
                  dormantDays: Number.isFinite(parsed) ? parsed : current.dormantDays,
                }));
              }}
            />
          </label>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem 0.5rem 0' }}>
                    {t('common.studio.live.chatAccessAudience')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>
                    {t('common.studio.live.chatAccessRule')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0' }}>
                    {t('common.studio.live.chatGrayCap')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {CHAT_AUDIENCE_TIERS.map((tier) => {
                  const rule = chatAccessPolicy.rules[tier];
                  return (
                    <tr key={tier}>
                      <td style={{ padding: '0.5rem 0.75rem 0.5rem 0', verticalAlign: 'top' }}>
                        {tierLabel(tier)}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'top' }}>
                        <select
                          className="fm-input"
                          value={rule.mode}
                          onChange={(event) => {
                            const mode = event.target.value as ChatAccessMode;
                            setChatAccessPolicy((current) =>
                              updateRule(current, tier, { mode }),
                            );
                          }}
                        >
                          {CHAT_ACCESS_MODES.map((mode) => (
                            <option key={mode} value={mode}>
                              {modeLabel(mode)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.5rem 0', verticalAlign: 'top' }}>
                        {rule.mode === 'MESSAGE_CAP' ? (
                          <input
                            className="fm-input"
                            type="number"
                            min={1}
                            max={50}
                            value={String(rule.messageCap ?? 3)}
                            onChange={(event) => {
                              const parsed = Number.parseInt(event.target.value, 10);
                              setChatAccessPolicy((current) =>
                                updateRule(current, tier, {
                                  messageCap: Number.isFinite(parsed) ? parsed : 3,
                                }),
                              );
                            }}
                          />
                        ) : (
                          <span style={{ color: 'var(--fm-color-text-muted)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h3 style={{ marginTop: '1.5rem', marginBottom: '0.35rem', fontSize: '1rem' }}>
            {t('common.studio.dashboard.privateChatPricingTitle')}
          </h3>
          <p style={{ marginTop: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
            {t('common.studio.dashboard.privateChatPricingHint')}
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={privateChatPricing.enabled}
              onChange={(event) => {
                setPrivateChatPricing((current) => ({
                  ...current,
                  enabled: event.target.checked,
                }));
              }}
            />
            {t('common.studio.dashboard.privateChatEnabled')}
          </label>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
              gap: '0.75rem',
              marginBottom: '0.5rem',
            }}
          >
            <label style={{ display: 'block', fontSize: '0.875rem' }}>
              {t('common.studio.dashboard.privateChatTextCoins')}
              <input
                className="fm-input"
                type="number"
                min={0}
                max={10000}
                disabled={!privateChatPricing.enabled}
                value={String(privateChatPricing.textCoins)}
                onChange={(event) => {
                  setPrivateChatPricing((current) => ({
                    ...current,
                    textCoins: parseCoinInput(event.target.value, current.textCoins),
                  }));
                }}
              />
            </label>
            <label style={{ display: 'block', fontSize: '0.875rem' }}>
              {t('common.studio.dashboard.privateChatImageCoins')}
              <input
                className="fm-input"
                type="number"
                min={0}
                max={10000}
                disabled={!privateChatPricing.enabled}
                value={String(privateChatPricing.imageCoins)}
                onChange={(event) => {
                  setPrivateChatPricing((current) => ({
                    ...current,
                    imageCoins: parseCoinInput(event.target.value, current.imageCoins),
                  }));
                }}
              />
            </label>
            <label style={{ display: 'block', fontSize: '0.875rem' }}>
              {t('common.studio.dashboard.privateChatStickerCoins')}
              <input
                className="fm-input"
                type="number"
                min={0}
                max={10000}
                disabled={!privateChatPricing.enabled}
                value={String(privateChatPricing.stickerCoins)}
                onChange={(event) => {
                  setPrivateChatPricing((current) => ({
                    ...current,
                    stickerCoins: parseCoinInput(event.target.value, current.stickerCoins),
                  }));
                }}
              />
            </label>
          </div>

          {error ? <p className="fm-error">{error}</p> : null}
          {success ? (
            <p style={{ color: 'var(--fm-color-link)', marginTop: '0.75rem' }}>
              {t('common.studio.live.chatSettingsSaved')}
            </p>
          ) : null}
          <div style={{ marginTop: '1rem' }}>
            <Button
              label={
                isSaving ? t('common.studio.live.chatSavingSettings') : t('common.studio.live.chatSaveSettings')
              }
              disabled={isSaving}
              onClick={() => {
                void handleSave();
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
